import React, { useState, useEffect, useRef, memo, useMemo, useImperativeHandle } from 'react'
import ChartWrapper from '../ChartWrapper'
import moment from 'moment'
import { useTranslation } from 'react-i18next';
import { mmHgToKPa, celsiusToFahrenheit, percentTommHg, percentToKpa, mmHgToPercent, checkEmptyValue, checkIBPZero, valueStringByType, isDetectingStringByType, levelStringByType, colorByPriority, getVSMFontSize, getNumericFontSize, getAlarmStringByType, unitStringByType } from '../../plugins/common'
import { useRecoilState, useRecoilValue } from 'recoil';
import { wardState, alarmsState, unitState, selectBedInfoState, wardSuspendState, wardSilenceState,globalParameterColorState } from '../../plugins/recoil';
import axios from '../../plugins/axios'

import AlarmDropdown from '../AlarmDropdown'
import useAlarm from './usePlayerAlarm';

import '../Patient.scss'

const Patient = ({ bedInfo, bedData, viewType, waveParameters, numericParameters, speed, isPlaying, oneBed = false, curSec }) => {
    const { t } = useTranslation()
    const [arr, setArr] = useState([0])
    const [dataCountArr, setDataCountArr] = useState([0])
    const [numeric, setNumeric] = useState([])
    const [vsData, setVsData] = useState({})
    // const [vsDetecting, setVsDetecting] = useState({})
    const [settingStatus, setSettingStatus] = useState({})
    const [outOfRange, setOutOfRange] = useState([])
    const [alarmLimitData, setAlarmLimitData] = useState({})
    const [alarms, ] = useRecoilState(alarmsState)
    const { alarmPriorities } = useAlarm(bedData, bedInfo);
    const highestPriority = alarms['player']?.length > 0 ? alarms['player'][0].type : null;
    const wardSuspend = useRecoilValue(wardSuspendState);
    const wardSilence = useRecoilValue(wardSilenceState);
    const isWardSilenced = wardSuspend === "2" && wardSilence.isSilenced;// 병동 Silence 여부
    const highIntervalRef = useRef(null)
    const mediumIntervalRef = useRef(null)
    const [highTypes, setHighTypes] = useState([])
    const [mediumTypes, setMediumTypes] = useState([])
    const [lowTypes, setLowTypes] = useState([])
    const [highHighlight, setHighHighlight] = useState(false)
    const [mediumHighlight, setMediumHighlight] = useState(false)
    const highHighlightRef = useRef(highHighlight)
    const mediumHighlightRef = useRef(mediumHighlight)
    const [lowHighlight, setLowHighlight] = useState(false)
    const [units, setUnits] = useRecoilState(unitState)
    const priorityOrder = { 'High': 1, 1: 'High', 'Medium': 2, 2:'Medium', 'Low': 3, 3: 'Low', 'Normal': 4, 4: 'Normal' };
    const [parameterColors, ] = useRecoilState(globalParameterColorState);
    const [isDetecting, setIsDetecting] = useState({ ...isDetectingStringByType })

    const getAlarmString = (vsType, onlyValue) => {
        const vitalSignType = vsType === 'awRR' ? 'RR' : vsType
        const highAlarmString = getAlarmStringByType(vitalSignType, 'high')
        const lowAlarmString = getAlarmStringByType(vitalSignType, 'low')
        
        if (onlyValue) {
            if (highAlarmString) return checkEmptyValue(settingStatus[highAlarmString])
            else return '---'
        }

        if (highAlarmString || lowAlarmString) {
            if (['NIBPSYS', 'NIBPDIA', 'NIBPMEAN', 'IBP1SYS', 'IBP1DIA', 'IBP1MEAN', 'IBP2SYS', 'IBP2DIA', 'IBP2MEAN', 'IBP3SYS', 'IBP3DIA', 'IBP3MEAN', 'IBP4SYS', 'IBP4DIA', 'IBP4MEAN', 'EtCO2', 'InCO2', 'Temp1', 'Temp2'].indexOf(vsType) !== -1) {
                const vsTypeText = vsType.replace('SYS', '').replace('DIA', '').replace('MEAN', '')
                if (units[vsTypeText] === 'kPa') {
                    return `${checkEmptyValue(mmHgToKPa(settingStatus[highAlarmString]))}/${checkEmptyValue(mmHgToKPa(settingStatus[lowAlarmString]))}`
                } else if (units[vsTypeText] === '%') {
                    return `${checkEmptyValue(mmHgToPercent(settingStatus[highAlarmString]))}/${checkEmptyValue(mmHgToPercent(settingStatus[lowAlarmString]))}`
                } else if (units[vsTypeText] === '°F') {
                    return `${checkEmptyValue(celsiusToFahrenheit(settingStatus[highAlarmString]))}/${checkEmptyValue(celsiusToFahrenheit(settingStatus[lowAlarmString]))}`
                } else if (units[vsTypeText] === '°C') {
                    return `${checkEmptyValue(settingStatus[highAlarmString]?.toFixed(1))}/${checkEmptyValue(settingStatus[lowAlarmString]?.toFixed(1))}`
                } else {
                    return `${checkEmptyValue(settingStatus[highAlarmString])}/${checkEmptyValue(settingStatus[lowAlarmString])}`
                }
            } else {
                return `${checkEmptyValue(settingStatus[highAlarmString])}/${checkEmptyValue(settingStatus[lowAlarmString])}`
            }
        } else {
            return `-/-`
        }
    }

    const handleUpdateDataCount = (idx, val) => {
        const newDataCountArr = [...dataCountArr]
        newDataCountArr[idx] = val
        setDataCountArr(newDataCountArr)
    }

    const getNIBPDiff = () => {
        if (!settingStatus.lastBPMonth || !settingStatus.lastBPDate) return '---'
        let nibpTime = moment()
                .set('month', settingStatus.lastBPMonth - 1)
                .set('date', settingStatus.lastBPDay)
                .set('hour', settingStatus.lastBPHour)
                .set('minute', settingStatus.lastBPMinute)
        while(nibpTime.isAfter(moment())) {
            nibpTime = nibpTime.subtract(1, 'year')
        }
        const diffTime = moment().diff(nibpTime)
        const duration = moment.duration(diffTime)
        const totalMinutes = Math.floor(duration.asMinutes())
        const hrs = Math.floor(totalMinutes / 60)
        const mins = totalMinutes % 60

        return `${hrs.toString().length > 1 ? hrs : '0' + hrs}:${mins.toString().length > 1 ? mins : '0' + mins}`
    }

    useEffect(() => {
        const filteredWaveParameters = waveParameters ? waveParameters.filter(item => item.measureParameter !== 'NONE') : []
        setArr(filteredWaveParameters.map((item, idx) => item.measureParameter))
        setDataCountArr(Array(filteredWaveParameters.length))
    }, [waveParameters])

    useEffect(() => {
        setNumeric(numericParameters.map((item, idx) => item.measureParameter))
    }, [numericParameters])

    useEffect(() => {
        if (bedData) {
            const newBedData = {
                numericData: bedData.numericData ? { ...bedData.numericData } : {},
                waveData: bedData.waveData ? { ...bedData.waveData } : {},
                settingData: bedData.settingData ? { ...bedData.settingData } : {}
                // outOfRange: []
            }
            const ibpTypeValueStringArr = [];
            ['NIBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4'].forEach((typeKey) => {
                ibpTypeValueStringArr.push(...[`${typeKey.toLowerCase()}DiaValue`, `${typeKey.toLowerCase()}SysValue`, `${typeKey.toLowerCase()}MapValue`])
            })

            Object.keys(bedData.numericData || {}).forEach(key => {
                // IBP일때는 0이거나 값이 없을때만 값 세팅
                if (ibpTypeValueStringArr.indexOf(key) !== -1 && (!newBedData.numericData[key])) {
                    newBedData.numericData[key] = bedData.numericData[key]
                } else {
                    newBedData.numericData[key] = bedData.numericData[key]
                }
            })

            if (bedData.outOfRangeData?.length > 0) {
                const newOutOfRange = []
                const outOfRangeSeries = ['HR', 'SpO2', 'RESP', 'IBP1', 'IBP2', 'IBP3', 'IBP4', 'Co2', 'GAS', 'Temp1', 'Temp2']
                const outOfRangeTypes = bedData.outOfRangeData.map((item) => item.alarmType)
                outOfRangeTypes.forEach((item) => {
                    if (item === 'ECG') { newOutOfRange.push('HR', 'PR')}
                    else if (item === 'SPO2') { newOutOfRange.push('SpO2', 'PR')}
                    else if (item === 'CO2') { newOutOfRange.push('EtCO2', 'InCO2', 'IPI') }
                    else if (item === 'TEMP1') { newOutOfRange.push('Temp1') }
                    else if (item === 'TEMP2') { newOutOfRange.push('Temp2') }
                    else { newOutOfRange.push(item) }
                })
                setOutOfRange(newOutOfRange)
            } else {
                setOutOfRange([])
            }

            const bpSeries = ['NIBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4']
            bpSeries.forEach((item) => {
                if (units[item] === 'kPa') {
                    newBedData.numericData[`${item.toLowerCase()}Sys`] = mmHgToKPa(newBedData.numericData[`${item.toLowerCase()}Sys`])
                    newBedData.numericData[`${item.toLowerCase()}Dia`] = mmHgToKPa(newBedData.numericData[`${item.toLowerCase()}Dia`])
                    newBedData.numericData[`${item.toLowerCase()}Map`] = mmHgToKPa(newBedData.numericData[`${item.toLowerCase()}Map`])
                }
            })

            const co2Series = ['EtCO2', 'InCO2']
            co2Series.forEach((item) => {
                if (units[item] === 'kPa') {
                    newBedData.numericData[valueStringByType[item]] = mmHgToKPa(newBedData.numericData[valueStringByType[item]])
                }else if (units[item] === '%') {
                    newBedData.numericData[valueStringByType[item]] = mmHgToPercent(newBedData.numericData[valueStringByType[item]])
                }
            })

            const tempSeries = ['Temp1', 'Temp2']
            tempSeries.forEach(item => {
                if (units[item] === '°F') {
                    newBedData.numericData[valueStringByType[item]] = celsiusToFahrenheit(newBedData.numericData[valueStringByType[item]])
                } else {
                    if (isNaN(parseFloat(newBedData.numericData[valueStringByType[item]])?.toFixed(1))) {
                        newBedData.numericData[valueStringByType[item]] = null
                    } else {
                        newBedData.numericData[valueStringByType[item]] = parseFloat(newBedData.numericData[valueStringByType[item]])?.toFixed(1)
                    }
                }
            })

            if (units['GAS'] === 'mmHg') {
                newBedData.numericData[valueStringByType['GasO2Ex']] = percentTommHg(newBedData.numericData[valueStringByType['GasO2Ex']])
                newBedData.numericData[valueStringByType['GasO2In']] = percentTommHg(newBedData.numericData[valueStringByType['GasO2In']])
                newBedData.numericData[valueStringByType['GasCo2Ex']] = percentTommHg(newBedData.numericData[valueStringByType['GasCo2Ex']])
                newBedData.numericData[valueStringByType['GasCo2In']] = percentTommHg(newBedData.numericData[valueStringByType['GasCo2In']])
                newBedData.numericData[valueStringByType['GasSevEx']] = percentTommHg(newBedData.numericData[valueStringByType['GasSevEx']])
                newBedData.numericData[valueStringByType['GasSevIn']] = percentTommHg(newBedData.numericData[valueStringByType['GasSevIn']])
                newBedData.numericData[valueStringByType['GasDesEx']] = percentTommHg(newBedData.numericData[valueStringByType['GasDesEx']])
                newBedData.numericData[valueStringByType['GasDesIn']] = percentTommHg(newBedData.numericData[valueStringByType['GasDesIn']])
                newBedData.numericData[valueStringByType['GasN2OEx']] = percentTommHg(newBedData.numericData[valueStringByType['GasN2OEx']])
                newBedData.numericData[valueStringByType['GasN2OIn']] = percentTommHg(newBedData.numericData[valueStringByType['GasN2OIn']])
            } else if (units['GAS'] === 'kPa') {
                newBedData.numericData[valueStringByType['GasO2Ex']] = percentToKpa(newBedData.numericData[valueStringByType['GasO2Ex']])
                newBedData.numericData[valueStringByType['GasO2In']] = percentToKpa(newBedData.numericData[valueStringByType['GasO2In']])
                newBedData.numericData[valueStringByType['GasCo2Ex']] = percentToKpa(newBedData.numericData[valueStringByType['GasCo2Ex']])
                newBedData.numericData[valueStringByType['GasCo2In']] = percentToKpa(newBedData.numericData[valueStringByType['GasCo2In']])
                newBedData.numericData[valueStringByType['GasSevEx']] = percentToKpa(newBedData.numericData[valueStringByType['GasSevEx']])
                newBedData.numericData[valueStringByType['GasSevIn']] = percentToKpa(newBedData.numericData[valueStringByType['GasSevIn']])
                newBedData.numericData[valueStringByType['GasDesEx']] = percentToKpa(newBedData.numericData[valueStringByType['GasDesEx']])
                newBedData.numericData[valueStringByType['GasDesIn']] = percentToKpa(newBedData.numericData[valueStringByType['GasDesIn']])
                newBedData.numericData[valueStringByType['GasN2OEx']] = percentToKpa(newBedData.numericData[valueStringByType['GasN2OEx']])
                newBedData.numericData[valueStringByType['GasN2OIn']] = percentToKpa(newBedData.numericData[valueStringByType['GasN2OIn']])
            }

            if (['M40', 'M50', 'M40/M50'].indexOf(bedInfo.deviceModel) !== -1) {
                if(newBedData.settingData.rrOnOff){
                    if (newBedData.numericData.rrSource === 'Impedance') { // ECG로 되어있으면 무조건 '-'
                        newBedData.numericData[valueStringByType['awRR']] = '-'
                    } else {
                        newBedData.numericData[valueStringByType['RR']] = '-'
                        newBedData.numericData[valueStringByType['RESP']] = '-'
                    }
    
                    if (newBedData.numericData.rrSource === 'Airway') {
                        setIsDetecting(prev => ({ ...prev, 'awRR': 'co2Measurement' }))
                        if (!newBedData.numericData.co2Measurement) {
                            newBedData.numericData[valueStringByType['awRR']] = '-'
                        }
                    } 
                    if (newBedData.numericData.rrSource === 'Airway2 (from Gas)') {
                        setIsDetecting(prev => ({ ...prev, 'awRR': 'gasCO2Measurement' }))
                        if (!newBedData.numericData.gasCO2Measurement) {
                            newBedData.numericData[valueStringByType['awRR']] = '-'
                        }
                    }
                }
            } else {
                if(newBedData.settingData.rrOnOff){
                    if (newBedData.numericData.rrSource === 'Airway') {
                        setIsDetecting(prev => ({ ...prev, 'awRR': 'co2Measurement' }))
                        if (!newBedData.numericData.co2Measurement) {
                            newBedData.numericData[valueStringByType['awRR']] = '-'
                        }
                    } 
                    if (newBedData.numericData.rrSource === 'Airway2 (from Gas)') {
                        setIsDetecting(prev => ({ ...prev, 'awRR': 'gasCO2Measurement' }))
                        if (!newBedData.numericData.gasCO2Measurement) {
                            newBedData.numericData[valueStringByType['awRR']] = '-'
                        }
                    }

                    if (newBedData.numericData.rrSource === 'Impedance') { // ECG로 되어있으면 무조건 '-'
                        newBedData.numericData[valueStringByType['awRR']] = '-'
                    } else {
                        newBedData.numericData[valueStringByType['RR']] = '-'
                        newBedData.numericData[valueStringByType['RESP']] = '-'
                    }
                }
            }
            

            setIsDetecting(prev => ({ ...prev, 'PR': `${newBedData.numericData.hrprSource?.toLowerCase()}Measurement` }))
            if (newBedData.numericData.hrprSource === 'ECG') {
                newBedData.numericData[valueStringByType['PR']] = newBedData.numericData.hrValue 
            } else if (['SpO2', 'IBP1', 'IBP2', 'IBP3', 'IBP4', 'NIBP'].includes(newBedData.numericData.hrprSource)) {
                if (['IBP1', 'IBP2', 'IBP3', 'IBP4'].includes(newBedData.numericData.hrprSource)) {
                    if (['PAP', 'ICP', 'CVP', 'RAP', 'LAP', 'UVP', 'IC1', 'IC2'].includes(newBedData.settingData[`${newBedData.numericData.hrprSource.toLowerCase()}Label`])) {
                        newBedData.numericData[valueStringByType['PR']] = '-'
                    } else {
                        newBedData.numericData[valueStringByType['PR']] = newBedData.numericData[`${newBedData.numericData.hrprSource.toLowerCase()}PulseRate`] 
                    }
                } else {
                    newBedData.numericData[valueStringByType['PR']] = newBedData.numericData[`${newBedData.numericData.hrprSource.toLowerCase()}PulseRate`] 
                    if (newBedData.numericData.hrprSource === 'NIBP') {
                        newBedData.numericData.nibpMeasurement = !(checkIBPZero(newBedData.numericData.nibpSysValue, '', '', true) === '-' && checkIBPZero(newBedData.numericData.nibpDiaValue, '', '', true) === '-' && checkIBPZero(newBedData.numericData.nibpMapValue, '', '', true) === '-')
                    }
                }
            }
            
            setSettingStatus(newBedData.settingData)   
            setVsData(newBedData.numericData)
            // console.log(newBedData.numericData)
        }
    }, [bedData])

    useEffect(() => {
        return () => {
            if (highIntervalRef.current) {
                clearInterval(highIntervalRef.current)
            }
            if (mediumIntervalRef.current) {
                clearInterval(mediumIntervalRef.current)
            }
        }
    }, [])

    const getAdaptiveFontSize = (value, baseVal = 50) => {
        const valueStr = String(value || '');
        const length = valueStr.length;
        
        // 기본 폰트 크기
        let fontSize = baseVal;
        
        // 길이에 따라 폰트 크기 조정

        if (length > 2) {
            fontSize = Math.max(20, baseVal - (length - 2) * 3);
        }
        return fontSize;
    };

    const PatientNumeric = memo(({ isLeft, type, alarmString, isVSM }) => {
        const backgroundColor = 'transparent';
        const defaultValue = ['Temp1', 'Temp2'].indexOf(type) !== -1 ? '--.-' : ''

        return (
            <>
            {
                ['NIBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4'].indexOf(type) !== -1 ?
                (
                    <div className={`flex flex-1 items-center px-2 py-2`} style={{ backgroundColor, color: parameterColors.numericParameterColors[type], fontFamily: 'Roboto', borderLeftWidth: isLeft ? 1 : 0, borderRightWidth: isLeft ? 1 : 0, borderColor: '#383838' }}>
                        <div className="flex flex-col h-full">
                            <div className="" style={{ ...getNumericFontSize(viewType, 'type') }}>{settingStatus[`${type.toLowerCase()}Label`] ? settingStatus[`${type.toLowerCase()}Label`] : type}</div>
                            <div className="mb-auto" style={{ ...getNumericFontSize(viewType, 'unit') }}>{checkEmptyValue(units[type])}</div>
                            {
                                ['NIBP'].indexOf(type) !== -1 &&
                                <div className="" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>{`${getNIBPDiff()}`}</div>
                            }
                        </div>
                        <div className="flex flex-col ml-auto" style={{  }}>
                            <div className="flex items-center mb-2 ml-auto">
                                {
                                    outOfRange.indexOf(type) !== -1 ?
                                    <div className="mr-1" style={{ ...getNumericFontSize(viewType, 'nibpValue') }}>{`XXX`}</div>
                                    :
                                    (
                                        type === 'NIBP' ?
                                        <>
                                            <div className="mr-1" style={{ ...getNumericFontSize(viewType, 'nibpValue') }}>{`${checkIBPZero(vsData[`${type.toLowerCase()}SysValue`], '' ,'', true)}/${checkIBPZero(vsData[`${type.toLowerCase()}DiaValue`], '' ,'', true)}`}</div>
                                            <div className="" style={{ ...getNumericFontSize(viewType, 'nibpSubValue') }}>{`(${checkIBPZero(vsData[`${type.toLowerCase()}MapValue`], '' ,'', true)})`}</div>
                                        </> :
                                        <>
                                            <div className="mr-1" style={{ ...getNumericFontSize(viewType, 'nibpValue') }}>{`${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}SysValue`], '' ,'', false) : '-'}/${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}DiaValue`], '' ,'', false) : '-'}`}</div>
                                            <div className="" style={{ ...getNumericFontSize(viewType, 'nibpSubValue') }}>{`(${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}MapValue`], '' ,'', false) : '-'})`}</div>
                                        </>
                                    )
                                }
                            </div>
                            <div className="flex ml-auto">
                                <div className="mr-1" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{getAlarmString(`${type}SYS`)}</div>
                                <div className="mr-1" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{getAlarmString(`${type}DIA`)}</div>
                                <div className="mr-1" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{getAlarmString(`${type}MEAN`)}</div>
                            </div>
                        </div>
                    </div>    
                )
                :
                ['GAS'].indexOf(type) !== -1 ? 
                <div className="flex flex-1 items-center px-2 py-2" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], borderLeftWidth: isLeft ? 1 : 0, borderRightWidth: isLeft ? 1 : 0, borderColor: '#383838' }}>
                    <div className="flex-1">
                        <div className="" style={{ ...getNumericFontSize(viewType, 'type') }}>{'GAS (Ex/In)'}</div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{`O2: ${vsData[isDetecting['GasO2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasO2Ex']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasO2']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasO2In']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{`N2O: ${vsData[isDetecting['GasN2O']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasN2OEx']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasN2O']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasN2OIn']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                    </div>
                    <div className="ml-auto text-right" style={oneBed ? { minWidth: 180, maxWidth: 180 } : { minWidth: 80, maxWidth: 80 }}>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{`CO2: ${vsData[isDetecting['GasCO2']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasCo2Ex']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasCO2']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasCo2In']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{`SEV: ${vsData[isDetecting['GasAA1']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasSevEx']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasAA1']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasSevIn']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'nibpAlarm') }}>{`DES: ${vsData[isDetecting['GasAA2']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasDesEx']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasAA2']] ?  (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasDesIn']]?.toFixed(settingStatus.gasUnit === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                    </div>
                </div>
                :
                ['BIS'].indexOf(type) !== -1 ? 
                <div className="flex items-center flex-1 px-2 py-2" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], borderLeftWidth: isLeft ? 1 : 0, borderRightWidth: isLeft ? 1 : 0, borderColor: '#383838' }}>
                    <div className="flex-1">
                        <div className="" style={{ ...getNumericFontSize(viewType, 'type') }}>{'BIS'}</div>
                        <div className="flex" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>
                            {`SQI: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.sqiValue) : '-'}`}
                            {/* <div className="ml-auto mr-2" style={{  }}>---</div> */}
                        </div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>{`EMG: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.emgValue) : '-'}`}</div>
                    </div>
                    <div className="ml-auto" style={{ minWidth: 70, maxWidth: 70 }}>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>{`SR: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.srValue) : '-'}`}</div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>{`SEF: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.sefValue) : '-'}`}</div>
                        <div className="" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>{`TP: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.tpValue) : '-'}`}</div>
                    </div>
                </div>
                :
                ['EWS'].indexOf(type) !== -1 ?
                (
                    <div className="flex flex-1 items-center px-2 py-2 text-[#FFFFFF]" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], borderLeftWidth: isLeft ? 1 : 0, borderRightWidth: isLeft ? 1 : 0, borderColor: '#383838' }}>
                        <div className="">
                            <div className="" style={{ ...getNumericFontSize(viewType, 'type')}}>
                                {type}
                            </div>
                            <div className="" style={{ ...getNumericFontSize(viewType, 'unit') }}>{checkEmptyValue(units[type])}</div>
                            <div className="" style={{ ...getNumericFontSize(viewType, 'alarmString') }}>{checkEmptyValue(vsData[levelStringByType[type]], 'Lv: ')}</div>
                        </div>
                        <div className="ml-auto" style={{ ...getNumericFontSize(viewType, 'value') }}>{checkEmptyValue(vsData[valueStringByType[type]] == '100' ? '-' : vsData[valueStringByType[type]])}</div>
                    </div>
                )
                :
                (
                    <div className="flex flex-1 items-center px-2 py-2 text-[#FFFFFF]" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], borderLeftWidth: isLeft ? 1 : 0, borderRightWidth: isLeft ? 1 : 0, borderColor: '#383838' }}>
                        <div className={`${oneBed ? 'flex flex-col h-full justify-evenly' : ''}`} style={['IPI'].indexOf(type) === -1 ? {} : { display: 'flex', flexDirection: 'column', height: '100%' }}>                            
                            <div>
                                {
                                    type === 'PR' ?
                                    <div className="" style={{ ...getNumericFontSize(viewType, 'type') }}>
                                        {['SpO2', 'IBP1', 'IBP2', 'IBP3', 'IBP4', 'NIBP'].includes(vsData.hrprSource) ? `PR (${vsData.hrprSource})` : 'PR'}
                                    </div> :
                                    <div className="" style={{ ...getNumericFontSize(viewType, 'type') }}>
                                        {type == 'RESP' ? 'RR' : type}
                                    </div>
                                }
                                {
                                    ['IPI'].indexOf(type) === -1 &&
                                    <>
                                        {
                                            ['EtCO2', 'InCO2', 'Temp1', 'Temp2'].indexOf(type) !== -1 ?
                                            <div className="" style={{ ...getNumericFontSize(viewType, 'unit') }}>{checkEmptyValue(units[type])}</div>
                                            :
                                            <div className="" style={{ ...getNumericFontSize(viewType, 'unit') }}>{unitStringByType[type] ? checkEmptyValue(settingStatus[unitStringByType[type]]) : checkEmptyValue(units[type])}</div>
                                        }
                                    </>
                                }
                            </div>                           
                                                        
                            <div className="" style={{ marginTop: oneBed ? 'auto' : (['IPI'].indexOf(type) === -1 ? '' : 'auto'), ...getNumericFontSize(viewType, 'alarmString') }}>{checkEmptyValue(alarmString)}</div>
                        </div>
                        <div className="ml-auto" style={{ ...getNumericFontSize(viewType, 'value'), fontSize: getAdaptiveFontSize(vsData[isDetecting[type]] ? (outOfRange.indexOf(type) !== -1 ? (['Temp1', 'Temp2'].indexOf(type) !== -1 ? 'XX.X' : 'XXX') : checkEmptyValue(vsData[valueStringByType[type]], '', '', defaultValue)) : (['Temp1', 'Temp2'].indexOf(type) !== -1 ? '--.-' : '-'), getNumericFontSize(viewType, 'value').fontSize) }}>{vsData[isDetecting[type]] ? (outOfRange.indexOf(type) !== -1 ? (['Temp1', 'Temp2'].indexOf(type) !== -1 ? 'XX.X' : 'XXX') : checkEmptyValue(vsData[valueStringByType[type]], '', '', defaultValue)) : (['Temp1', 'Temp2'].indexOf(type) !== -1 ? '--.-' : '-')}</div>
                    </div>
                )
            }
            </>
        )
    })

    return (
        <div className={`relative flex flex-grow h-full`} style={{ backgroundColor: (alarms['player']?.length > 0) ? '#02008B' : 'black', width: '100%' }}>
            {
                <div className="flex flex-col flex-grow" style={{ height: '100%', maxWidth: 'calc(100% - 448px)' }} >
                    <div className="flex items-center" style={{ height: 32 }}>
                        <div className="flex items-center justify-center h-full text-white" style={{ fontWeight: 500, fontSize: 24, lineHeight: '24px', minWidth: 178, maxWidth: 178, background: 'linear-gradient(180deg, #5E5E5E 0%, #383838 100%)' }}>
                            {checkEmptyValue(bedInfo.bedNumber.length <= 1 ? `0${bedInfo.bedNumber}` : bedInfo.bedNumber, `${t('dashboard.bedName')}-`)}
                        </div>
                        <div className="flex items-center" style={{ width: 'calc(100% - 178px)' }}>
                            <div className="relative flex-1 text-white truncate" style={{ marginLeft: 5, maxWidth: 'calc(100% - 28px)', fontSize: 16, fontWeight: '17.6px', fontWeight: 200 }} title={ bedInfo.nameDisplay === '1' ? checkEmptyValue(bedInfo.patientName) : bedInfo.nameDisplay === '2' ? checkEmptyValue(bedInfo.devicePatientId) : bedInfo.nameDisplay === '3' ? `${checkEmptyValue(bedInfo.patientName)},${checkEmptyValue(bedInfo.devicePatientId)}` : '-'}>                                        
                                {!bedInfo.patientId ? `${t('dashboard.notAdmitted')}` : (bedInfo.nameDisplay === '1' ? checkEmptyValue(bedInfo.patientName) : bedInfo.nameDisplay === '2' ? checkEmptyValue(bedInfo.devicePatientId) : bedInfo.nameDisplay === '3' ? `${checkEmptyValue(bedInfo.patientName)},${checkEmptyValue(bedInfo.devicePatientId)}` : '-')}
                            </div>
                        </div>
                    </div>
                    {
                        <div className="relative" style={{ height: 'calc(100% - 32px)'}}>
                            {
                                arr.map((node, idx) => {
                                    let isDetectingValue = typeof vsData[isDetecting[node]] === 'undefined' ? true : vsData[isDetecting[node]]
                                    if (node === 'RESP') { // RESP 표시는 ecgMeasurement true 이면서 rrOnOff가 true 일 때로 변경
                                        isDetectingValue = bedData?.numericData?.ecgMeasurement && bedData?.settingData?.rrOnOff
                                    }
                                    return (
                                        <ChartWrapper 
                                            key={`chart-item-${viewType}-${idx}`}
                                            isFirst={idx === 0}
                                            isPlayer={true}
                                            isDisconnected={false}
                                            isBedNumberBig={false}
                                            isDetecting={isDetectingValue}
                                            isShowECGPacer={true}
                                            settingStatus={settingStatus}
                                            speed={speed}
                                            curSec={curSec}
                                            isPlaying={isPlaying}
                                            type={node}
                                            data={bedData ? bedData.waveData || {} : {}}
                                            bedInfo={bedInfo}
                                            chartCount={arr.length}
                                            viewType={viewType}
                                            handleBufferCount={(bufferCnt) => { handleUpdateDataCount(idx, bufferCnt) }}
                                        />
                                    )
                                })
                            }
                        </div>
                    }
                </div>
            }
            <div className="relative flex" style={{ paddingTop: ((alarms['player']?.length) > 0 ? 0 : 32), flexDirection: 'column', height: '100%', borderRightWidth: 1, borderColor: 'black', minWidth: `${'448px' }`, maxWidth: `${'448px' }` }}>                    
                {
                    alarms['player']?.length > 0 &&  <div className='flex-1' style={{ minHeight: 32, maxHeight: 32 }}>
                        <AlarmDropdown options={alarms['player'] || []} isPlayer={true} priority={highestPriority} placeholder="알람 선택" />
                    </div>
                }
                <div className="flex flex-1" style={{ borderTopWidth: 1, borderBottomWidth: 1, borderColor: '#383838', height: alarms['player']?.length > 0 ? `calc((100% - 32px) / 6)` : `${100 / 6}%` }}>
                    <PatientNumeric isLeft={true} type={numeric[0]} alarmString={getAlarmString(numeric[0])}/>
                    <PatientNumeric isLeft={false} type={numeric[6]} alarmString={getAlarmString(numeric[6])}/>
                </div>
                <div className="flex flex-1" style={{  borderBottomWidth: 1, borderColor: '#383838', height: alarms['player']?.length > 0 ? `calc((100% - 32px) / 6)` : `${100 / 6}%` }}>
                    <PatientNumeric isLeft={true} type={numeric[1]} alarmString={getAlarmString(numeric[1])}/>
                    <PatientNumeric isLeft={false} type={numeric[7]} alarmString={getAlarmString(numeric[7])}/>
                </div>
                <div className="flex flex-1" style={{  borderBottomWidth: 1, borderColor: '#383838', height: alarms['player']?.length > 0 ? `calc((100% - 32px) / 6)` : `${100 / 6}%` }}>
                    <PatientNumeric isLeft={true} type={numeric[2]} alarmString={getAlarmString(numeric[2])}/>
                    <PatientNumeric isLeft={false} type={numeric[8]} alarmString={getAlarmString(numeric[8])}/>
                </div>
                <div className="flex flex-1" style={{  borderBottomWidth: 1, borderColor: '#383838', height: alarms['player']?.length > 0 ? `calc((100% - 32px) / 6)` : `${100 / 6}%` }}>
                    <PatientNumeric isLeft={true} type={numeric[3]} alarmString={getAlarmString(numeric[3])}/>
                    <PatientNumeric isLeft={false} type={numeric[9]} alarmString={getAlarmString(numeric[9])}/>
                </div>
                <div className="flex flex-1" style={{  borderBottomWidth: 1, borderColor: '#383838', height: alarms['player']?.length > 0 ? `calc((100% - 32px) / 6)` : `${100 / 6}%` }}>
                    <PatientNumeric isLeft={true} type={numeric[4]} alarmString={getAlarmString(numeric[4])}/>
                    <PatientNumeric isLeft={false} type={numeric[10]} alarmString={getAlarmString(numeric[10])}/>
                </div>
                <div className="flex flex-1" style={{  borderBottomWidth: 1, borderColor: '#383838', height: alarms['player']?.length > 0 ? `calc((100% - 32px) / 6)` : `${100 / 6}%` }}>
                    <PatientNumeric isLeft={true} type={numeric[5]} alarmString={getAlarmString(numeric[5])}/>
                    <PatientNumeric isLeft={false} type={numeric[11]} alarmString={getAlarmString(numeric[11])}/>
                </div>
            </div>
        </div>
    )
}

export default Patient